import { Loader2 } from "lucide-react";
import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import VideoBlogLayout from "../components/VideoBlogLayout";
import { getApiBaseUrl } from "../lib/gatedContentTracking";
import NotFoundPage from "./NotFoundPage";

const renderBodyParagraphs = (body) =>
  String(body || "")
    .split(/\n{2,}/)
    .map((paragraph) => paragraph.trim())
    .filter(Boolean)
    .map((paragraph) => (
      <p key={paragraph} className="text-lg text-gray-600 leading-relaxed mb-4 last:mb-0">
        {paragraph}
      </p>
    ));

const GeneratedVideoBlogPage = () => {
  const { slug } = useParams();
  const [page, setPage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    let isMounted = true;

    const fetchPage = async () => {
      setLoading(true);
      setNotFound(false);
      setPage(null);

      try {
        const response = await fetch(
          `${getApiBaseUrl()}/api/blog/generated/${encodeURIComponent(slug)}`
        );
        const data = await response.json().catch(() => null);

        if (!response.ok) {
          if (response.status === 404) {
            if (isMounted) {
              setNotFound(true);
            }
            return;
          }
          throw new Error(data?.error || "Failed to load blog page.");
        }

        if (isMounted) {
          setPage(data);
        }
      } catch (error) {
        console.error("Failed to load generated blog page", error);
        if (isMounted) {
          setNotFound(true);
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    if (slug) {
      fetchPage();
    } else {
      setNotFound(true);
      setLoading(false);
    }

    return () => {
      isMounted = false;
    };
  }, [slug]);

  if (loading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-8 h-8 text-blue-600 animate-spin mx-auto mb-4" />
          <p className="text-gray-500 text-sm">Loading video...</p>
        </div>
      </div>
    );
  }

  if (notFound || !page) {
    return <NotFoundPage />;
  }

  return (
    <VideoBlogLayout
      category={page.category}
      eyebrow={page.eyebrow}
      title={page.title}
      subtitle={page.subtitle}
      author={page.author}
      readTime={page.readTime}
      videoUrl={page.loomEmbedUrl}
      videoTitle={page.videoTitle}
      videoProvider={page.videoProvider}
    >
      <div className="space-y-14">
        {(page.bodySections || []).map((section, index) => (
          <div key={`${section.heading || "section"}-${index}`}>
            {section.heading && (
              <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-4 tracking-tight">
                {section.heading}
              </h2>
            )}
            {renderBodyParagraphs(section.body)}
          </div>
        ))}
      </div>
    </VideoBlogLayout>
  );
};

export default GeneratedVideoBlogPage;
